import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const LotSizeFinderCalculator = () => {
  const content = article.find((item) => item.id === 9);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [marketType, setMarketType] = useState("forex"); // forex, stocks, futures, options

  const [inputs, setInputs] = useState({
    // Account information
    accountBalance: "10000",
    accountCurrency: "USD",
    riskPercent: "1",
    
    // Trade information
    instrument: "EUR/USD",
    lotSize: "100000",
    contractSize: "100000",
    pointValue: "10",
    
    // Risk management
    stopLossPips: "50",
    stopLossPoints: "50",
    stopLossPrice: "0.0050",
    entryPrice: "1.2000",
    
    // Advanced settings
    commission: "5.00",
    swapRate: "0.50",
    spread: "1.2",
    slippage: "0.5",
    
    // Portfolio constraints
    maxLotsPerTrade: "10",
    maxDailyLots: "50",
    correlationFactor: "0.3",
    
    // Market specific
    marginRequirement: "2",
    leverage: "100",
    marginUsed: "0",
    
    // Common inputs
    currency: "1",
    volatility: "10"
  });

  const [instruments, setInstruments] = useState([
    { symbol: "EUR/USD", lotSize: "100000", pointValue: "10", marginReq: "2" },
    { symbol: "GBP/USD", lotSize: "100000", pointValue: "10", marginReq: "2" },
    { symbol: "USD/JPY", lotSize: "100000", pointValue: "1000", marginReq: "2" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateLotSize();
    }
  }, [inputs, instruments, marketType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleInstrumentChange = (index, field, value) => {
    const updatedInstruments = instruments.map((instrument, i) => 
      i === index ? { ...instrument, [field]: value } : instrument
    );
    setInstruments(updatedInstruments);
  };

  const addInstrument = () => {
    setInstruments([...instruments, { symbol: "", lotSize: "100000", pointValue: "10", marginReq: "2" }]);
  };

  const removeInstrument = (index) => {
    if (instruments.length > 1) {
      setInstruments(instruments.filter((_, i) => i !== index));
    }
  };

  const calculateLotSize = () => {
    const currencyRate = parseFloat(inputs.currency) || 1;
    const accountBalance = parseFloat(inputs.accountBalance) || 0;
    const riskPercent = parseFloat(inputs.riskPercent) || 0;
    const stopLossPips = parseFloat(inputs.stopLossPips) || 0;
    const stopLossPoints = parseFloat(inputs.stopLossPoints) || 0;
    const stopLossPrice = parseFloat(inputs.stopLossPrice) || 0;
    const entryPrice = parseFloat(inputs.entryPrice) || 0;
    const pointValue = parseFloat(inputs.pointValue) || 0;
    const lotSize = parseFloat(inputs.lotSize) || 0;
    const commission = parseFloat(inputs.commission) || 0;
    const spread = parseFloat(inputs.spread) || 0;
    const slippage = parseFloat(inputs.slippage) || 0;
    const swapRate = parseFloat(inputs.swapRate) || 0;
    const leverage = parseFloat(inputs.leverage) || 1;
    const marginRequirement = parseFloat(inputs.marginRequirement) || 0;

    let riskAmount = 0;
    let lotSizeResult = 0;
    let positionSize = 0;
    let pipValue = 0;
    let riskPerLot = 0;

    // Calculate based on market type
    switch (marketType) {
      case "forex":
        riskAmount = (riskPercent / 100) * accountBalance;
        riskPerLot = stopLossPips * pointValue;
        
        if (riskPerLot > 0) {
          lotSizeResult = riskAmount / riskPerLot;
        }
        
        pipValue = pointValue * lotSizeResult;
        positionSize = lotSizeResult * lotSize;
        break;

      case "stocks":
        riskAmount = (riskPercent / 100) * accountBalance;
        const priceRisk = Math.abs(entryPrice - stopLossPrice);
        riskPerLot = priceRisk * lotSize;
        
        if (riskPerLot > 0) {
          lotSizeResult = riskAmount / riskPerLot;
        }
        
        positionSize = lotSizeResult * lotSize;
        pipValue = priceRisk * positionSize;
        break;

      case "futures":
        riskAmount = (riskPercent / 100) * accountBalance;
        riskPerLot = stopLossPoints * pointValue;
        
        if (riskPerLot > 0) {
          lotSizeResult = riskAmount / riskPerLot;
        }
        
        positionSize = lotSizeResult;
        pipValue = pointValue * lotSizeResult;
        break;

      case "options":
        riskAmount = (riskPercent / 100) * accountBalance;
        const contractRisk = Math.abs(entryPrice - stopLossPrice) * 100; // Options are typically 100 shares
        riskPerLot = contractRisk;
        
        if (riskPerLot > 0) {
          lotSizeResult = riskAmount / contractRisk;
        }
        
        positionSize = Math.floor(lotSizeResult) * 100; // Round to whole contracts
        pipValue = contractRisk;
        break;
    }

    // Apply constraints
    const maxLotsPerTrade = parseFloat(inputs.maxLotsPerTrade) || 1000;
    const maxDailyLots = parseFloat(inputs.maxDailyLots) || 5000;
    
    lotSizeResult = Math.min(lotSizeResult, maxLotsPerTrade);
    lotSizeResult = Math.min(lotSizeResult, maxDailyLots);

    // Calculate margin requirements
    const marginPerLot = positionSize / leverage;
    const totalMargin = marginPerLot * lotSizeResult;
    const marginUtilization = (totalMargin / accountBalance) * 100;

    // Calculate costs
    const totalCommission = commission * lotSizeResult;
    const totalSpread = (spread / 10000) * positionSize; // Assuming spread in pips
    const totalSlippage = (slippage / 10000) * positionSize;
    const totalSwap = swapRate * lotSizeResult;

    const totalCosts = totalCommission + totalSpread + totalSlippage + totalSwap;

    // Risk metrics
    const riskRewardRatio = 3; // Default, can be calculated based on take profit
    const potentialLoss = riskAmount;
    const potentialProfit = potentialLoss * riskRewardRatio;
    const breakEvenPoints = totalCosts / pipValue;

    // Volatility adjustment
    const volatility = parseFloat(inputs.volatility) || 0;
    const volatilityAdjustment = 1 + (volatility / 100);
    const adjustedLotSize = lotSizeResult / volatilityAdjustment;

    const resultData = {
      // Lot size results
      lotSize: lotSizeResult,
      adjustedLotSize: adjustedLotSize,
      roundedLotSize: Math.floor(lotSizeResult * 100) / 100, // Round to 2 decimal places for micro lots
      standardLots: Math.floor(lotSizeResult),
      miniLots: Math.floor((lotSizeResult - Math.floor(lotSizeResult)) * 10),
      microLots: Math.floor((lotSizeResult * 100) % 100),
      
      // Risk metrics
      riskAmount: riskAmount * currencyRate,
      riskPerLot: riskPerLot * currencyRate,
      pipValue: pipValue * currencyRate,
      positionSize: positionSize * currencyRate,
      
      // Margin calculations
      marginPerLot: marginPerLot * currencyRate,
      totalMargin: totalMargin * currencyRate,
      marginUtilization,
      
      // Cost breakdown
      totalCommission: totalCommission * currencyRate,
      totalSpread: totalSpread * currencyRate,
      totalSlippage: totalSlippage * currencyRate,
      totalSwap: totalSwap * currencyRate,
      totalCosts: totalCosts * currencyRate,
      breakEvenPoints,
      
      // Profit/loss projections
      potentialLoss: potentialLoss * currencyRate,
      potentialProfit: potentialProfit * currencyRate,
      riskRewardRatio,
      
      // Market specifics
      marketType,
      currencyRate,
      leverage,
      volatility,
      volatilityAdjustment
    };

    setResult(resultData);
  };

  const calculateLotSizeBreakdown = () => {
    if (!result) return [];

    return [
      { type: "Standard Lots", size: 1, count: result.standardLots, value: result.standardLots },
      { type: "Mini Lots", size: 0.1, count: result.miniLots, value: result.miniLots * 0.1 },
      { type: "Micro Lots", size: 0.01, count: result.microLots, value: result.microLots * 0.01 },
      { type: "Remaining", size: 0.0001, count: Math.round((result.lotSize - result.roundedLotSize) * 10000), value: result.lotSize - result.roundedLotSize }
    ].filter(item => item.count > 0);
  };

  const calculateRiskScenarios = () => {
    if (!result) return [];

    const scenarios = [
      { pips: 10, probability: 20, label: "Small Move" },
      { pips: 25, probability: 35, label: "Normal Move" },
      { pips: 50, probability: 25, label: "Large Move" },
      { pips: 100, probability: 15, label: "Major Move" },
      { pips: 200, probability: 5, label: "Extreme Move" }
    ];

    return scenarios.map(scenario => {
      const profitLoss = scenario.pips * result.pipValue;
      const isProfit = profitLoss >= 0;
      const expectedValue = (profitLoss * scenario.probability) / 100;
      
      return {
        ...scenario,
        profitLoss,
        isProfit,
        expectedValue,
        accountImpact: (profitLoss / parseFloat(inputs.accountBalance)) * 100
      };
    });
  };

  const calculateLeverageAnalysis = () => {
    if (!result) return [];

    const leverageLevels = [10, 20, 30, 50, 100, 200, 400, 500];
    
    return leverageLevels.map(leverage => {
      const marginRequired = result.positionSize / leverage;
      const marginUtilization = (marginRequired / parseFloat(inputs.accountBalance)) * 100;
      const maxLotsWithLeverage = (parseFloat(inputs.accountBalance) * (parseFloat(inputs.riskPercent) / 100)) / result.riskPerLot;
      
      return {
        leverage,
        marginRequired,
        marginUtilization,
        maxLots: maxLotsWithLeverage,
        isSafe: marginUtilization <= 50
      };
    });
  };

  const resetCalculator = () => {
    setInstruments([
      { symbol: "EUR/USD", lotSize: "100000", pointValue: "10", marginReq: "2" },
      { symbol: "GBP/USD", lotSize: "100000", pointValue: "10", marginReq: "2" },
      { symbol: "USD/JPY", lotSize: "100000", pointValue: "1000", marginReq: "2" }
    ]);
    setInputs({
      accountBalance: "10000",
      accountCurrency: "USD",
      riskPercent: "1",
      instrument: "EUR/USD",
      lotSize: "100000",
      contractSize: "100000",
      pointValue: "10",
      stopLossPips: "50",
      stopLossPoints: "50",
      stopLossPrice: "0.0050",
      entryPrice: "1.2000",
      commission: "5.00",
      swapRate: "0.50",
      spread: "1.2",
      slippage: "0.5",
      maxLotsPerTrade: "10",
      maxDailyLots: "50",
      correlationFactor: "0.3",
      marginRequirement: "2",
      leverage: "100",
      marginUsed: "0",
      currency: "1",
      volatility: "10"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Optimal Lot Size,${result.lotSize.toFixed(4)}`,
      `Adjusted Lot Size,${result.adjustedLotSize.toFixed(4)}`,
      `Standard Lots,${result.standardLots}`,
      `Mini Lots,${result.miniLots}`,
      `Micro Lots,${result.microLots}`,
      `Risk Amount,${result.riskAmount.toFixed(2)}`,
      `Risk Per Lot,${result.riskPerLot.toFixed(2)}`,
      `Pip Value,${result.pipValue.toFixed(2)}`,
      `Position Size,${result.positionSize.toFixed(2)}`,
      `Total Margin,${result.totalMargin.toFixed(2)}`,
      `Margin Utilization,${result.marginUtilization.toFixed(2)}%`,
      `Total Costs,${result.totalCosts.toFixed(2)}`,
      `Break-even Points,${result.breakEvenPoints.toFixed(2)}`,
      `Potential Loss,${result.potentialLoss.toFixed(2)}`,
      `Potential Profit,${result.potentialProfit.toFixed(2)}`,
      `Risk/Reward Ratio,${result.riskRewardRatio.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lot-size-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      instruments: [...instruments],
      marketType,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setInstruments(calc.instruments || []);
    setMarketType(calc.marketType || "forex");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        marketType: "forex",
        inputs: { 
          accountBalance: "5000", 
          riskPercent: "0.5",
          leverage: "50",
          stopLossPips: "30"
        }
      },
      moderate: { 
        marketType: "forex",
        inputs: { 
          accountBalance: "10000", 
          riskPercent: "1",
          leverage: "100",
          stopLossPips: "50"
        }
      },
      aggressive: { 
        marketType: "forex",
        inputs: { 
          accountBalance: "25000", 
          riskPercent: "2",
          leverage: "200",
          stopLossPips: "80"
        }
      }
    };
    
    const presetData = presets[preset];
    setMarketType(presetData.marketType);
    setInputs(prev => ({ ...prev, ...presetData.inputs }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const lotSizeBreakdownData = calculateLotSizeBreakdown();
  const breakdownChartData = lotSizeBreakdownData.length > 0 ? {
    labels: lotSizeBreakdownData.map(item => item.type),
    datasets: [{
      data: lotSizeBreakdownData.map(item => item.value),
      backgroundColor: ["#19692C", "#28A745", "#2B8C3A", "#FFD93D"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const riskScenarioData = calculateRiskScenarios();
  const riskChartData = riskScenarioData.length > 0 ? {
    labels: riskScenarioData.map(s => s.label),
    datasets: [{
      label: 'Profit/Loss',
      data: riskScenarioData.map(s => s.profitLoss),
      backgroundColor: riskScenarioData.map(s => s.isProfit ? '#28A745' : '#E53E3E'),
      borderColor: riskScenarioData.map(s => s.isProfit ? '#28A745' : '#E53E3E'),
      borderWidth: 2
    }]
  } : null;

  const leverageData = calculateLeverageAnalysis();
  const leverageChartData = leverageData.length > 0 ? {
    labels: leverageData.map(l => `${l.leverage}:1`),
    datasets: [
      {
        label: 'Margin Required',
        data: leverageData.map(l => l.marginRequired),
        backgroundColor: "#19692C",
        borderColor: "#19692C",
        borderWidth: 2
      },
      {
        label: 'Margin Utilization %',
        data: leverageData.map(l => l.marginUtilization),
        backgroundColor: "#FF6B6B",
        borderColor: "#FF6B6B",
        borderWidth: 2,
        yAxisID: 'y1'
      }
    ]
  } : null;

  const costBreakdownData = result ? {
    labels: ["Commission", "Spread", "Slippage", "Swap"],
    datasets: [{
      data: [
        result.totalCommission,
        result.totalSpread,
        result.totalSlippage,
        result.totalSwap
      ],
      backgroundColor: ["#19692C", "#FFD93D", "#FF6B6B", "#4ECDC4"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Lot Size Finder Calculator</h1>
        </div>
        <p>Calculate optimal lot sizes, manage position sizing, and optimize risk management across different markets</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        {calc.lotSize?.toFixed(4)} lots
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Lot Size Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "instruments" ? "active" : ""}`}
                onClick={() => setActiveTab("instruments")}
              >
                <i className="fas fa-exchange-alt"></i>
                {isMobile ? 'Instruments' : 'Instruments'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Risk Profile Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Market Type Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${marketType === "forex" ? "active" : ""}`}
                onClick={() => setMarketType("forex")}
              >
                <i className="fas fa-globe"></i>
                Forex
              </button>
              <button 
                className={`mode-btn ${marketType === "stocks" ? "active" : ""}`}
                onClick={() => setMarketType("stocks")}
              >
                <i className="fas fa-chart-line"></i>
                Stocks
              </button>
              <button 
                className={`mode-btn ${marketType === "futures" ? "active" : ""}`}
                onClick={() => setMarketType("futures")}
              >
                <i className="fas fa-chess-board"></i>
                Futures
              </button>
              <button 
                className={`mode-btn ${marketType === "options" ? "active" : ""}`}
                onClick={() => setMarketType("options")}
              >
                <i className="fas fa-chess-knight"></i>
                Options
              </button>
            </div>
          </div>

          {/* Lot Size Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Account Information */}
                  <h3>
                    <i className="fas fa-piggy-bank"></i>
                    Account Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Account Balance</label>
                      <input 
                        type="number" 
                        name="accountBalance" 
                        value={inputs.accountBalance} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Risk Per Trade (%)</label>
                      <input 
                        type="number" 
                        name="riskPercent" 
                        value={inputs.riskPercent} 
                        onChange={handleChange}
                        placeholder="1"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Account Currency</label>
                      <select name="accountCurrency" value={inputs.accountCurrency} onChange={handleChange}>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="JPY">JPY</option>
                        <option value="CAD">CAD</option>
                        <option value="AUD">AUD</option>
                      </select>
                    </div>
                  </div>

                  {/* Trade Setup */}
                  <h3>
                    <i className="fas fa-exchange-alt"></i>
                    Trade Setup
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Instrument</label>
                      <input 
                        type="text" 
                        name="instrument" 
                        value={inputs.instrument} 
                        onChange={handleChange}
                        placeholder="EUR/USD"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-cube"></i> Standard Lot Size</label>
                      <input 
                        type="number" 
                        name="lotSize" 
                        value={inputs.lotSize} 
                        onChange={handleChange}
                        placeholder="100000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-contract"></i> Contract Size</label>
                      <input 
                        type="number" 
                        name="contractSize" 
                        value={inputs.contractSize} 
                        onChange={handleChange}
                        placeholder="100000"
                      />
                    </div>
                  </div>

                  {/* Risk Management */}
                  <h3>
                    <i className="fas fa-shield-alt"></i>
                    Risk Management
                  </h3>
                  <div className="input-row">
                    {marketType === "forex" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-arrows-alt-h"></i> Stop Loss (Pips)</label>
                        <input 
                          type="number" 
                          name="stopLossPips" 
                          value={inputs.stopLossPips} 
                          onChange={handleChange}
                          placeholder="50"
                          step="1"
                        />
                      </div>
                    )}
                    {marketType === "futures" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-arrows-alt-h"></i> Stop Loss (Points)</label>
                        <input 
                          type="number" 
                          name="stopLossPoints" 
                          value={inputs.stopLossPoints} 
                          onChange={handleChange}
                          placeholder="50"
                          step="1"
                        />
                      </div>
                    )}
                    {(marketType === "stocks" || marketType === "options") && (
                      <>
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                          <input 
                            type="number" 
                            name="entryPrice" 
                            value={inputs.entryPrice} 
                            onChange={handleChange}
                            placeholder="1.2000"
                            step="0.0001"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                          <input 
                            type="number" 
                            name="stopLossPrice" 
                            value={inputs.stopLossPrice} 
                            onChange={handleChange}
                            placeholder="0.0050"
                            step="0.0001"
                          />
                        </div>
                      </>
                    )}
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Pip/Point Value</label>
                      <input 
                        type="number" 
                        name="pointValue" 
                        value={inputs.pointValue} 
                        onChange={handleChange}
                        placeholder="10"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Costs & Fees */}
                  <h3>
                    <i className="fas fa-receipt"></i>
                    Trading Costs
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Commission per Lot</label>
                      <input 
                        type="number" 
                        name="commission" 
                        value={inputs.commission} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-alt-h"></i> Spread (Pips)</label>
                      <input 
                        type="number" 
                        name="spread" 
                        value={inputs.spread} 
                        onChange={handleChange}
                        placeholder="1.2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-random"></i> Slippage (Pips)</label>
                      <input 
                        type="number" 
                        name="slippage" 
                        value={inputs.slippage} 
                        onChange={handleChange}
                        placeholder="0.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exchange-alt"></i> Swap Rate per Lot</label>
                      <input 
                        type="number" 
                        name="swapRate" 
                        value={inputs.swapRate} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="10">10:1</option>
                        <option value="20">20:1</option>
                        <option value="30">30:1</option>
                        <option value="50">50:1</option>
                        <option value="100">100:1</option>
                        <option value="200">200:1</option>
                        <option value="400">400:1</option>
                        <option value="500">500:1</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Margin Requirement %</label>
                      <input 
                        type="number" 
                        name="marginRequirement" 
                        value={inputs.marginRequirement} 
                        onChange={handleChange}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Volatility %</label>
                      <input 
                        type="number" 
                        name="volatility" 
                        value={inputs.volatility} 
                        onChange={handleChange}
                        placeholder="10"
                        step="0.1"
                      />
                    </div>
                  </div>

                  {/* Portfolio Constraints */}
                  <h3>
                    <i className="fas fa-chart-pie"></i>
                    Portfolio Constraints
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-cube"></i> Max Lots Per Trade</label>
                      <input 
                        type="number" 
                        name="maxLotsPerTrade" 
                        value={inputs.maxLotsPerTrade} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Max Daily Lots</label>
                      <input 
                        type="number" 
                        name="maxDailyLots" 
                        value={inputs.maxDailyLots} 
                        onChange={handleChange}
                        placeholder="50"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-link"></i> Correlation Factor</label>
                      <input 
                        type="number" 
                        name="correlationFactor" 
                        value={inputs.correlationFactor} 
                        onChange={handleChange}
                        placeholder="0.3"
                        step="0.1"
                        min="0"
                        max="1"
                      />
                    </div>
                  </div>

                  {/* Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Instruments Tab */}
          {activeTab === "instruments" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-exchange-alt"></i>
                  Trading Instruments
                </h3>
                
                {instruments.map((instrument, index) => (
                  <div key={index} className="purchase-group">
                    <br/><div className="purchase-header">
                      <h4>Instrument #{index + 1}</h4>
                      {instruments.length > 1 && (
                        <button 
                          className="remove-purchase-btn"
                          onClick={() => removeInstrument(index)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div><br/>
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-tag"></i> Symbol</label>
                        <input 
                          type="text" 
                          value={instrument.symbol}
                          onChange={(e) => handleInstrumentChange(index, 'symbol', e.target.value)}
                          placeholder="EUR/USD"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-cube"></i> Lot Size</label>
                        <input 
                          type="number" 
                          value={instrument.lotSize}
                          onChange={(e) => handleInstrumentChange(index, 'lotSize', e.target.value)}
                          placeholder="100000"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-coins"></i> Pip Value</label>
                        <input 
                          type="number" 
                          value={instrument.pointValue}
                          onChange={(e) => handleInstrumentChange(index, 'pointValue', e.target.value)}
                          placeholder="10"
                          step="0.01"
                        />
                      </div>
                      <div className="input-field">
                        <label><i className="fa-solid fa-percentage"></i> Margin %</label>
                        <input 
                          type="number" 
                          value={instrument.marginReq}
                          onChange={(e) => handleInstrumentChange(index, 'marginReq', e.target.value)}
                          placeholder="2"
                          step="0.1"
                        />
                      </div>
                    </div><br/>
                  </div>
                ))}

                <button className="btn add-purchase-btn" onClick={addInstrument}>
                  <i className="fas fa-plus"></i>
                  Add Another Instrument
                </button>

                <div className="constraints-info">
                  <h4>Common Instrument Settings</h4>
                  <div className="info-list">
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Forex Majors:</strong> Lot Size: 100,000, Pip Value: ~$10</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Forex Minors:</strong> Lot Size: 100,000, Pip Value: varies</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Futures:</strong> Contract sizes vary by instrument</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-info-circle"></i>
                      <span><strong>Stocks:</strong> Lot size typically 100 shares</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-cube"></i>
                          {calc.lotSize?.toFixed(4)} lots
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Risk: ${calc.riskAmount?.toFixed(2)}</span>
                        <span><i className="fas fa-chart-pie"></i> Position: ${calc.positionSize?.toFixed(2)}</span>
                        <span><i className="fas fa-chess-board"></i> Market: {calc.marketType}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "instruments") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-cube"></i>
                    Lot Size Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          {result.lotSize.toFixed(4)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-cube"></i>
                          Optimal Lot Size
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.adjustedLotSize.toFixed(4)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bolt"></i>
                          Volatility Adjusted
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.riskAmount.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-shield-alt"></i>
                          Risk Amount
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.pipValue.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-coins"></i>
                          Pip Value
                        </div>
                      </div>
                    </div>

                    {/* Lot Size Breakdown */}
                    <div className="risk-summary">
                      <h4>Lot Size Breakdown</h4>
                      <div className="lot-breakdown">
                        <div className="lot-item">
                          <span className="lot-type">Standard Lots:</span>
                          <span className="lot-value">{result.standardLots}</span>
                        </div>
                        <div className="lot-item">
                          <span className="lot-type">Mini Lots:</span>
                          <span className="lot-value">{result.miniLots}</span>
                        </div>
                        <div className="lot-item">
                          <span className="lot-type">Micro Lots:</span>
                          <span className="lot-value">{result.microLots}</span>
                        </div>
                        <div className="lot-item">
                          <span className="lot-type">Total Lots:</span>
                          <span className="lot-value">{result.roundedLotSize.toFixed(4)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Position Size:</span>
                        <span>${result.positionSize.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-balance-scale"></i> Total Margin:</span>
                        <span>${result.totalMargin.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Margin Utilization:</span>
                        <span>{result.marginUtilization.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-rocket"></i> Leverage:</span>
                        <span>{result.leverage}:1</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bolt"></i> Volatility Adjustment:</span>
                        <span>{result.volatilityAdjustment.toFixed(2)}x</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrows-alt-h"></i> Break-even Points:</span>
                        <span>{result.breakEvenPoints.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Risk Management Summary */}
                    <div className="risk-summary">
                      <h4>Risk Management</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.potentialLoss.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Loss</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value positive">
                            ${result.potentialProfit.toFixed(2)}
                          </div>
                          <div className="risk-label">Potential Profit</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.riskRewardRatio.toFixed(2)}:1
                          </div>
                          <div className="risk-label">Risk/Reward Ratio</div>
                        </div>
                      </div>
                    </div>

                    {/* Lot Size Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Lot Size Composition
                      </h3>
                      <Doughnut 
                        data={breakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.label}: ${context.parsed.toFixed(4)} lots`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Cost Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Trading Cost Breakdown
                      </h3>
                      <Doughnut 
                        data={costBreakdownData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Risk Scenario Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Risk Scenario Analysis
                      </h3>
                      <Bar 
                        data={riskChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Leverage Analysis Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-rocket"></i>
                        Leverage Impact Analysis
                      </h3>
                      <Bar 
                        data={leverageChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              title: {
                                display: true,
                                text: 'Margin Required ($)'
                              }
                            },
                            y1: {
                              position: 'right',
                              title: {
                                display: true,
                                text: 'Margin Utilization (%)'
                              },
                              grid: {
                                drawOnChartArea: false
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Detailed Risk Scenarios */}
                    <div className="risk-summary">
                      <h4>Detailed Risk Scenarios</h4>
                      <div className="scenario-list">
                        {riskScenarioData.map((scenario, index) => (
                          <div key={index} className={`scenario-item ${scenario.isProfit ? 'positive' : 'negative'}`}>
                            <div className="scenario-header">
                              <span className="scenario-name">{scenario.label}</span>
                              <span className="scenario-pips">{scenario.pips} pips</span>
                            </div>
                            <div className="scenario-details">
                              <span>P/L: ${scenario.profitLoss.toFixed(2)}</span>
                              <span>Account: {scenario.accountImpact.toFixed(2)}%</span>
                              <span>Probability: {scenario.probability}%</span>
                              <span>Expected: ${scenario.expectedValue.toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Leverage Safety Analysis */}
                    <div className="risk-summary">
                      <h4>Leverage Safety Analysis</h4>
                      <div className="leverage-list">
                        {leverageData.map((level, index) => (
                          <div key={index} className={`leverage-item ${level.isSafe ? 'safe' : 'risky'}`}>
                            <div className="leverage-header">
                              <span className="leverage-level">{level.leverage}:1 Leverage</span>
                              <span className={`leverage-status ${level.isSafe ? 'positive' : 'negative'}`}>
                                {level.isSafe ? 'SAFE' : 'RISKY'}
                              </span>
                            </div>
                            <div className="leverage-details">
                              <span>Max Lots: {level.maxLots.toFixed(2)}</span>
                              <span>Margin: ${level.marginRequired.toFixed(2)}</span>
                              <span>Utilization: {level.marginUtilization.toFixed(2)}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-cube fa-3x"></i>
                    <p>Enter your trading parameters to calculate optimal lot size</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "instruments" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-exchange-alt"></i>
                  Instrument Analysis
                </h3>
                
                <div className="instruments-summary">
                  <div className="summary-card">
                    <div className="summary-value">{instruments.length}</div>
                    <div className="summary-label">
                      <i className="fas fa-exchange-alt"></i>
                      Total Instruments
                    </div> 
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">{marketType.toUpperCase()}</div>
                    <div className="summary-label">
                      <i className="fas fa-globe"></i>
                      Market Type
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">{inputs.accountCurrency}</div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Account Currency
                    </div>
                  </div><br/>
                </div>

                <div className="instruments-details">
                  <h4>Instrument Specifications</h4>
                  <div className="instruments-list">
                    {instruments.map((instrument, index) => (
                      <div key={index} className="instrument-item">
                        <div className="instrument-header">
                          <span className="instrument-symbol">{instrument.symbol || 'Unknown'}</span>
                          <span className="instrument-lot-size">{instrument.lotSize} units</span>
                        </div>
                        <div className="instrument-details">
                          <span>Pip Value: ${instrument.pointValue}</span>
                          <span>Margin: {instrument.marginReq}%</span>
                          <span>Risk/Lot: ${(parseFloat(instrument.pointValue) * parseFloat(inputs.stopLossPips)).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div><br/>
                </div>

                <div className="trading-tips">
                  <h4>Trading Tips</h4><br/>
                  <div className="tips-list">
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Always calculate lot size based on your risk tolerance</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Consider volatility when adjusting position sizes</span>
                    </div>
                    <div className="tip-item warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>High leverage can amplify both profits and losses</span>
                    </div>
                    <div className="tip-item positive">
                      <i className="fas fa-lightbulb"></i>
                      <span>Factor in trading costs (spread, commission, swap) for accurate calculations</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default LotSizeFinderCalculator;